import { TranslateService } from "@ngx-translate/core";
import { DialogRef, DialogService } from "@ngneat/dialog";
import { toggleAnimation } from "src/app/shared/animations";
import { Component, inject, OnInit, TemplateRef, ViewChild } from "@angular/core";
import { AbstractControl, FormBuilder, FormGroup, ValidationErrors, Validators } from "@angular/forms";
import { User, UserToUpdate } from "src/app/auth/model/user";
import { UserService } from "../users/service/user.service";
import { Store } from "@ngrx/store";
import { updateUser } from "src/app/store/auth/auth.actions";
import { showMessage } from "src/app/shared/utils/toast.popup";
import { PopupType } from "src/app/shared/enums/popup-types";

function checkRequiredFields(control: AbstractControl): ValidationErrors | null {
  const keys = Object.keys(control.value);
  const requiredKeys = keys.filter(key => (control.get(key)?.errors as any)?.required);

  return requiredKeys.length === 0 ? null : { requiredFields: true };
}

@Component({
  templateUrl: "./user-profile.component.html",
  animations: [toggleAnimation],
  standalone: false,
})
export class UserSettingsComponent implements OnInit {
  activeTab = "settings";
  @ViewChild("modalTemplate") modalTemplate!: TemplateRef<unknown>;
  email!: string;

  contractId = 0;

  userSettingsForm!: FormGroup;
  agencySettingsForm!: FormGroup;
  editProfile = false;
  localUser!: User;
  isLoading = false;

  modal!: DialogRef;
  private dialog = inject(DialogService);

  constructor(private fb: FormBuilder, public translate: TranslateService, private userService: UserService, public store: Store<any>) {}

  ngOnInit() {
    const load = localStorage.getItem("user");
    if (load) {
      this.localUser = JSON.parse(load);
    }
    this.email = this.localUser.email;
    this.userSettingsForm = this.fb.group(
      {
        name: [{ value: this.localUser.name, disabled: true }, Validators.required],
        surname: [{ value: this.localUser.surname, disabled: true }, Validators.required],
        email: [{ value: this.localUser.email, disabled: true }, [Validators.required, Validators.email]],
        role: [{ value: this.localUser.role, disabled: true }, Validators.required],
      },
      { validator: checkRequiredFields },
    );
  }

  editUser() {
    this.editProfile = true;
    this.setFormState();
  }

  private setFormState() {
    if (this.editProfile) {
      this.userSettingsForm.get("name")?.enable();
      this.userSettingsForm.get("surname")?.enable();
    }
    else {
      this.userSettingsForm.markAsUntouched();
      this.userSettingsForm.get("name")?.disable();
      this.userSettingsForm.get("surname")?.disable();
    }
  }

  discardChanges() {
    this.userSettingsForm.markAsUntouched();
    this.userSettingsForm.get("name")?.setValue(this.localUser.name);
    this.userSettingsForm.get("surname")?.setValue(this.localUser.surname);
    this.userSettingsForm.get("name")?.disable();
    this.userSettingsForm.get("surname")?.disable();
    this.editProfile = false;
  }

  passwordUpdated(successful: boolean) {
    if (successful) {
      this.modal.close();
    }
  }

  updateUser() {
    if (!this.userSettingsForm.valid) {
      this.userSettingsForm.markAllAsTouched();
      return;
    }

    const { name, surname, role } = this.userSettingsForm.getRawValue();

    const data = {
      name,
      surname,
      role,
    } as UserToUpdate;

    this.isLoading = true;
    const updateSubscription = this.userService.updateUser(this.localUser.id, data).subscribe({
      next: (user) => {
        updateSubscription.unsubscribe();
        this.isLoading = false;
        this.store.dispatch(updateUser({ user }));
        this.editProfile = false;
        this.setFormState();
        showMessage(PopupType.Success, this.translate.instant("user_profile.successfull_update"));
      },
      error: (error) => {
        updateSubscription.unsubscribe();
        this.isLoading = false;
        showMessage(PopupType.Danger, error.message);
      },
    });
  }

  openPasswordModal() {
    this.modal = this.dialog.open(this.modalTemplate, {
      id: `dialog-${crypto.randomUUID()}`,
    });
  }
}
